import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type * as React from 'react'

import { cn } from '@/utils/cn'

const badgeVariants = cva(
  'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    variants: {
      variant: {
        default: 'bg-primary/10 text-primary border-primary/20 [a]:hover:bg-primary/20',
        secondary: 'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        destructive:
          'bg-destructive/10 text-destructive border-destructive/20 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        outline:
          'border-border bg-background text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost: 'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
        link: 'text-primary underline-offset-4 hover:underline',
        // Status variants with subtle backgrounds - connected to theme variables
        success:
          'bg-success/10 text-success-foreground border-success/20 dark:text-success dark:bg-success/20',
        warning:
          'bg-warning/10 text-warning-foreground border-warning/20 dark:text-warning dark:bg-warning/20',
        info: 'bg-info/10 text-info-foreground border-info/20 dark:text-info dark:bg-info/20',
        muted: 'bg-muted text-muted-foreground border-border/50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

const statusDotVariants = cva('size-1.5 rounded-full', {
  variants: {
    status: {
      active: 'bg-success',
      draft: 'bg-warning',
      closed: 'bg-muted-foreground',
      error: 'bg-destructive',
      pending: 'bg-info',
    },
  },
  defaultVariants: {
    status: 'active',
  },
})

function StatusDot({
  status,
  className,
}: {
  status?: 'active' | 'draft' | 'closed' | 'error' | 'pending'
  className?: string
}) {
  return <span className={cn(statusDotVariants({ status }), className)} />
}

export { Badge, badgeVariants, StatusDot, statusDotVariants }
